/**
 * Web Push for the visitor side.
 *
 * A push subscription requires a service worker, and a service worker must be
 * served from the *host* origin — a cross-origin script cannot install one.
 * So this only runs when the merchant opts in by pointing the snippet at a
 * worker they serve themselves (`data-service-worker="/superchat-sw.js"`).
 * Without it the widget degrades to socket-only delivery, which is fine while
 * the tab is open.
 */

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function notificationsSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Asks for notification permission.
 *
 * Returns the resulting permission, or `null` when the browser refused to even
 * show the prompt. That is deliberately distinct from a decline: a refusal is
 * worth retrying from a user gesture, a decline is not. Three things cause it:
 *
 *   - Firefox and Safari only honour this inside a user-generated event.
 *   - Chrome ignores it outside a "short running" event handler.
 *   - Chrome refuses outright in a cross-origin iframe, and no retry helps.
 *
 * Safari before 16 has no promise form, hence the callback bridge.
 */
export async function requestNotificationPermission({ debug = false } = {}) {
  if (!notificationsSupported()) return null;
  if (Notification.permission !== 'default') return Notification.permission;

  try {
    const result = await new Promise((resolve, reject) => {
      try {
        const maybe = Notification.requestPermission(resolve);
        if (maybe && typeof maybe.then === 'function') maybe.then(resolve, reject);
      } catch (err) {
        reject(err);
      }
    });

    const permission = result ?? Notification.permission;
    // Still 'default' means the prompt never appeared — treat as retryable.
    return permission === 'default' ? null : permission;
  } catch (err) {
    if (debug) console.warn('[superchat] notification prompt refused', err);
    return null;
  }
}

/**
 * Registers the host's worker and hands the subscription to the API. Returns
 * null whenever push is unavailable or permission is not already granted —
 * never throws for an ordinary "no", because the widget must keep working
 * without it. Asking is `requestNotificationPermission`'s job, not this one's:
 * permission is useful on its own for foreground notifications, whereas a push
 * subscription additionally needs a worker the merchant hosts.
 */
export async function enablePush({ api, serviceWorkerPath, vapidPublicKey, debug = false }) {
  if (!serviceWorkerPath || !vapidPublicKey || !pushSupported()) return null;
  if (Notification.permission !== 'granted') return null;

  try {
    const registration = await navigator.serviceWorker.register(serviceWorkerPath);
    await navigator.serviceWorker.ready;

    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        // Required by Chrome, and the right default anyway: never send a push
        // that renders no notification.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      }));

    const json = subscription.toJSON();
    await api.subscribePush({ endpoint: json.endpoint, keys: json.keys });
    return subscription;
  } catch (err) {
    if (debug) console.warn('[superchat] push unavailable', err);
    return null;
  }
}

/**
 * Foreground notification for a backgrounded tab.
 *
 * The server only Web Pushes when the visitor has no live socket. A merchant
 * with the admin open in another tab still has a socket, so without this they
 * would get nothing until they looked. Rendering through the service worker
 * registration keeps `tag` replacement consistent with pushed notifications.
 */
export async function showLocalNotification({ title, body, tag, isEmergency = false }) {
  if (!notificationsSupported() || Notification.permission !== 'granted') return false;
  // If they are looking at the page, the widget UI is the notification.
  if (document.visibilityState === 'visible' && !isEmergency) return false;

  const options = { body, tag, renotify: isEmergency, requireInteraction: isEmergency };

  try {
    const registration =
      'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : null;

    if (registration) {
      await registration.showNotification(title, options);
      return true;
    }

    // No worker on this origin, because the merchant did not host one. The
    // constructor still works on desktop — which is where a store admin is —
    // so permission alone is worth having even with push unavailable.
    new Notification(title, options);
    return true;
  } catch {
    return false;
  }
}

export async function disablePush({ api, subscription }) {
  if (!subscription) return;
  try {
    await api.unsubscribePush(subscription.endpoint);
    await subscription.unsubscribe();
  } catch {
    /* the endpoint is already gone; the server prunes 404/410 on its own */
  }
}

export default {
  enablePush,
  disablePush,
  pushSupported,
  notificationsSupported,
  requestNotificationPermission,
};
